import { ActorType } from '../enums/actorType';
import { useActorForm } from '../hooks/useActorForm';
import { RefObject, useEffect, useMemo, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { ActorLayoutVariant } from '../enums/actorLayoutVariant';
import { ActorDynamicLayout } from './form-layout/ActorDynamicLayout';
import { ActorFormConfig } from '../types/actorFormConfig';
import { FormInitialData } from 'types/form/formInitialData';
import { ActorFormValues } from '../hooks/optim/useSubmitActorForm';
import { LegalEntityOptions } from 'types/form/baseLayoutProps';

const ValidationTracker = ({
  isValid,
  onValidationChange
}: {
  isValid: boolean;
  onValidationChange: (isValid: boolean) => void;
}) => {
  const [lastValidState, setLastValidState] = useState(isValid);
  useEffect(() => {
    if (isValid !== lastValidState) {
      setLastValidState(isValid);
      onValidationChange(isValid);
    }
  }, [isValid, lastValidState, onValidationChange]);
  return null;
};

interface ActorFormProps<T extends ActorFormValues> {
  formConfig: ActorFormConfig;
  formikRef: RefObject<FormikProps<T>>;
  layoutVariant?: ActorLayoutVariant
  /** i18n keys overriding the layout variant title (create / edit). */
  titleKeys?: { create: string; edit: string };
  /** Opt-in blocks of the legal entity layout (off by default). */
  legalEntityOptions?: LegalEntityOptions;
  onValidationChange: (isValid: boolean) => void;
  onSubmit?: (values: T, actorType: ActorType) => void;
  editConfig?: {
    initialValues?: FormInitialData<ActorType, T>,
    initialData?: any,
    onSubmit: (values: Partial<T>, initialValues: Partial<T>, actorType: ActorType) => void;
  }
}

export const ActorForm = <T extends ActorFormValues>({
  layoutVariant = ActorLayoutVariant.DefaultActor,
  titleKeys,
  legalEntityOptions,
  formConfig,
  onSubmit,
  editConfig,
  formikRef,
  onValidationChange
}: ActorFormProps<T>) => {
  const isEdit = useMemo(() => !!editConfig, [editConfig]);

  const { actorType, handleActorTypeChange, currentFormConfig, savedValues, mergedInitialValues } =
    useActorForm(formConfig, editConfig?.initialValues);

  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  useEffect(() => {
    if (isInitialLoadComplete) {
      onValidationChange(true)
    }
  }, [isInitialLoadComplete, onValidationChange])

  useEffect(() => {
    if (savedValues[actorType] && !isInitialLoadComplete) {
      setTimeout(() => {
        setIsInitialLoadComplete(true);
      }, 0);
    }
  }, [savedValues, actorType, isInitialLoadComplete]);

  const handleSubmit = (values: T) => {
    if (editConfig?.onSubmit && editConfig.initialValues) {
      // Référence de comparaison = ce que Formik a réellement reçu (défauts fusionnés),
      // sinon un formulaire non touché diffère toujours du mapping brut (null vs "").
      const formInitialValues = mergedInitialValues[actorType] ?? editConfig.initialValues[actorType];
      editConfig.onSubmit(values as Partial<T>, formInitialValues as Partial<T>, actorType);
    } else {
      onSubmit?.(values, actorType);
    }
  };

  return (
    <Formik
      key={actorType}
      enableReinitialize
      initialValues={savedValues[actorType]}
      validationSchema={currentFormConfig.validationSchema}
      validateOnMount={isInitialLoadComplete}
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {({ values, setValues, isValid }) => (
        <>
          <ValidationTracker isValid={isValid} onValidationChange={onValidationChange} />
          <Form>
            <ActorDynamicLayout
              layoutVariant={layoutVariant}
              titleKeys={titleKeys}
              legalEntityOptions={legalEntityOptions}
              isEdit={isEdit}
              actorType={actorType}
              handleActorTypeChange={(type: ActorType) =>
                handleActorTypeChange(type, values, setValues)
              }
            />
          </Form>
        </>
      )}
    </Formik>
  );
};
